import { createContext, useContext, useState, useCallback } from 'react';

const EnterpriseContext = createContext(null);

const ROLES = {
  coder: { label: 'Coder', color: '#3b82f6', desc: 'Reviews charts and assigns ICD-10/CPT codes' },
  biller: { label: 'Biller', color: '#6b5ce7', desc: 'Reviews coded claims, runs AI validation, submits claims' },
  denials: { label: 'Denials Specialist', color: '#ef4444', desc: 'Works only denied/rejected claims and appeals' },
  supervisor: { label: 'Billing Supervisor', color: '#22c55e', desc: 'Full team visibility, KPI dashboard' },
  admin: { label: 'Admin', color: '#f59e0b', desc: 'Full access to all settings and views' },
};

const MOCK_TEAM = [
  { id: 'U001', name: 'Sarah Mitchell', email: 'sarah.m@thriving.med', role: 'biller', avatar: 'SM', providers: ['Dr. Anil Mehta', 'Dr. James Wilson'], status: 'active' },
  { id: 'U002', name: 'Jennifer Reyes', email: 'jennifer.r@thriving.med', role: 'coder', avatar: 'JR', providers: ['Dr. Anil Mehta', 'Dr. Sarah Park'], status: 'active' },
  { id: 'U003', name: 'Marcus Johnson', email: 'marcus.j@thriving.med', role: 'denials', avatar: 'MJ', providers: [], status: 'active' },
  { id: 'U004', name: 'Linda Chen', email: 'linda.c@thriving.med', role: 'supervisor', avatar: 'LC', providers: [], status: 'active' },
  { id: 'U005', name: 'David Park', email: 'david.p@thriving.med', role: 'biller', avatar: 'DP', providers: ['Dr. Sarah Park'], status: 'active' },
  { id: 'U006', name: 'Rachel Thompson', email: 'rachel.t@thriving.med', role: 'coder', avatar: 'RT', providers: ['Dr. James Wilson'], status: 'inactive' },
];

const MOCK_PROVIDERS = [
  'Dr. Anil Mehta', 'Dr. Sarah Park', 'Dr. James Wilson',
];

const MOCK_KPI = {
  firstPassRate: 96.2, firstPassTrend: 1.4,
  daysInAR: 28, arTarget: 30,
  denialRate: 4.8, denialTrend: -0.6, industryAvg: 10,
  openClaims: 34, pendingOver30: 7,
};

const MOCK_TEAM_PERF = [
  { name: 'Sarah Mitchell', role: 'Biller', claimsProcessed: 142, firstPass: 97.2, avgTime: 4.2, denialRate: 2.1, openItems: 3 },
  { name: 'Jennifer Reyes', role: 'Coder', claimsProcessed: 168, firstPass: 95.8, avgTime: 3.8, denialRate: 3.5, openItems: 5 },
  { name: 'David Park', role: 'Biller', claimsProcessed: 98, firstPass: 93.4, avgTime: 5.1, denialRate: 5.2, openItems: 8 },
  { name: 'Marcus Johnson', role: 'Denials', claimsProcessed: 45, firstPass: null, avgTime: 12.6, denialRate: null, openItems: 12 },
];

const MOCK_PROVIDER_PERF = [
  { name: 'Dr. Anil Mehta', specialty: 'Internal Medicine', claims: 86, denialRate: 3.2, topDenial: 'Missing modifier', revenue: 128500, pending: 18200 },
  { name: 'Dr. Sarah Park', specialty: 'Endocrinology', claims: 64, denialRate: 5.8, topDenial: 'Medical necessity', revenue: 96800, pending: 12400 },
  { name: 'Dr. James Wilson', specialty: 'Family Medicine', claims: 52, denialRate: 4.1, topDenial: 'Authorization', revenue: 78300, pending: 9600 },
];

const MOCK_DENIAL_TRENDS = [
  { category: 'Medical necessity', count: 7 },
  { category: 'Coding error', count: 4 },
  { category: 'Authorization', count: 5 },
  { category: 'Timely filing', count: 2 },
  { category: 'Duplicate', count: 1 },
  { category: 'Administrative', count: 3 },
];

const MOCK_ATTENTION_CLAIMS = [
  { id: 'CLM-4931', patient: 'Thomas Wright', provider: 'Dr. Anil Mehta', reason: '> 30 days unworked', days: 42, amount: 342, assignedTo: null },
  { id: 'CLM-4928', patient: 'Nancy Kim', provider: 'Dr. Sarah Park', reason: 'High-value denial ($500+)', days: 18, amount: 680, assignedTo: 'Marcus Johnson' },
  { id: 'CLM-4935', patient: 'Charles Davis', provider: 'Dr. James Wilson', reason: 'Appeal deadline approaching', days: 28, amount: 215, assignedTo: null },
  { id: 'CLM-4939', patient: 'Betty Martinez', provider: 'Dr. Anil Mehta', reason: 'Repeatedly denied', days: 55, amount: 425, assignedTo: 'Marcus Johnson' },
];

const MOCK_DENIED_CLAIMS = [
  {
    id: 'DEN-001', patient: 'Thomas Wright', provider: 'Dr. Anil Mehta', dos: '02/15/2026', amount: 342,
    denialReason: 'Missing modifier', reasonCategory: 'Coding', denialDate: '03/01/2026', daysSince: 42,
    payer: 'Blue Cross', carc: 'CARC 4', carcPlain: 'Blue Cross says modifier 25 is required when billing E/M code 99214 with a same-day procedure. The claim was denied because the modifier was missing.',
    resolution: ['Add modifier 25 to CPT 99214', 'Verify the procedure (36415) was documented as separately identifiable', 'Resubmit with corrected CMS-1500'],
    appealLetter: 'Dear Claims Department,\n\nI am writing to appeal the denial of claim DEN-001 for patient Thomas Wright (DOB: 09/15/1968), date of service 02/15/2026.\n\nThe E/M service (99214) was a significant, separately identifiable service performed on the same date as the venipuncture (36415). The documentation clearly supports that the evaluation and management service involved a detailed history, examination, and medical decision-making of moderate complexity, which is distinct from the blood draw procedure.\n\nModifier 25 has been added to the corrected claim. Please reconsider this claim for payment.\n\nSincerely,\nThriving Medical Group',
    dx: [{ code: 'E11.65', desc: 'Type 2 DM with hyperglycemia' }],
    lines: [{ cpt: '99214', charge: 185, mod: ['', '', '', ''] }, { cpt: '36415', charge: 18, mod: ['', '', '', ''] }],
    patientInfo: { name: 'Thomas Wright', dob: '09/15/1968', memberId: 'BC-7712345' },
    insuranceInfo: { payer: 'Blue Cross', policyNo: 'BC-7712345' },
  },
  {
    id: 'DEN-002', patient: 'Nancy Kim', provider: 'Dr. Sarah Park', dos: '02/20/2026', amount: 680,
    denialReason: 'Medical necessity', reasonCategory: 'Medical necessity', denialDate: '03/10/2026', daysSince: 18,
    payer: 'Cigna', carc: 'CO-50', carcPlain: 'Cigna says this nerve conduction study (95907) isn\'t medically necessary for the submitted diagnosis. Review the documentation to ensure the clinical indication is clearly stated, or consider an alternative diagnosis code that better supports the procedure.',
    resolution: ['Review documentation for clinical indication of nerve conduction study', 'Consider adding diagnosis E11.42 (diabetic polyneuropathy) as primary', 'Submit appeal with supporting clinical notes'],
    appealLetter: 'Dear Appeals Department,\n\nI am writing to appeal the denial of claim DEN-002 for patient Nancy Kim (DOB: 03/22/1958), date of service 02/20/2026.\n\nThe nerve conduction study (CPT 95907) was performed to evaluate diabetic polyneuropathy (ICD-10 E11.42), a known complication of the patient\'s Type 2 diabetes. Clinical examination revealed diminished sensation in bilateral lower extremities with absent ankle reflexes, warranting objective nerve conduction testing.\n\nEnclosed are the clinical notes documenting the neurological findings.\n\nSincerely,\nThriving Medical Group',
    dx: [{ code: 'E11.42', desc: 'DM with diabetic polyneuropathy' }],
    lines: [{ cpt: '95907', charge: 680, mod: ['', '', '', ''] }],
    patientInfo: { name: 'Nancy Kim', dob: '03/22/1958', memberId: 'CIG-9988765' },
    insuranceInfo: { payer: 'Cigna', policyNo: 'CIG-9988765' },
  },
  {
    id: 'DEN-003', patient: 'Charles Davis', provider: 'Dr. James Wilson', dos: '02/10/2026', amount: 215,
    denialReason: 'Authorization', reasonCategory: 'Authorization', denialDate: '02/28/2026', daysSince: 28,
    payer: 'Aetna', carc: 'CARC 15', carcPlain: 'Aetna says prior authorization was required for this service but was not obtained before the date of service. Contact payer to verify if a retroactive authorization can be requested.',
    resolution: ['Contact Aetna to request retroactive authorization', 'Provide medical records showing urgency/emergency if applicable', 'Resubmit with authorization number once obtained'],
    appealLetter: null,
    dx: [{ code: 'M54.5', desc: 'Low back pain' }],
    lines: [{ cpt: '20552', charge: 215, mod: ['', '', '', ''] }],
    patientInfo: { name: 'Charles Davis', dob: '11/05/1972', memberId: 'AET-5567890' },
    insuranceInfo: { payer: 'Aetna', policyNo: 'AET-5567890' },
  },
];

const STORAGE_KEY = 'ariaone_enterprise';

const loadState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { isEnterprise: false, currentRole: 'biller' };
};

const saveState = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch {}
};

export const EnterpriseProvider = ({ children }) => {
  const [state, setState] = useState(() => loadState());

  const setEnterprise = useCallback((enabled) => {
    setState(prev => {
      const next = { ...prev, isEnterprise: enabled };
      saveState(next);
      return next;
    });
  }, []);

  const setRole = useCallback((role) => {
    setState(prev => {
      const next = { ...prev, currentRole: role };
      saveState(next);
      return next;
    });
  }, []);

  const value = {
    isEnterprise: state.isEnterprise,
    currentRole: state.currentRole,
    setEnterprise,
    setRole,
    roles: ROLES,
    teamMembers: MOCK_TEAM,
    providers: MOCK_PROVIDERS,
    kpi: MOCK_KPI,
    teamPerf: MOCK_TEAM_PERF,
    providerPerf: MOCK_PROVIDER_PERF,
    denialTrends: MOCK_DENIAL_TRENDS,
    attentionClaims: MOCK_ATTENTION_CLAIMS,
    deniedClaims: MOCK_DENIED_CLAIMS,
  };

  return <EnterpriseContext.Provider value={value}>{children}</EnterpriseContext.Provider>;
};

export const useEnterprise = () => {
  const ctx = useContext(EnterpriseContext);
  if (!ctx) return { isEnterprise: false, currentRole: 'biller', roles: ROLES, teamMembers: [], providers: [], kpi: MOCK_KPI, teamPerf: [], providerPerf: [], denialTrends: [], attentionClaims: [], deniedClaims: [] };
  return ctx;
};

export { ROLES };
export default EnterpriseContext;
